import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { TaxReturnStatusBadge } from '../components/tax/TaxReturnStatusBadge';
import { FileText, Plus, Filter, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { taxReturnsAPI } from '../lib/taxReturnsAPI';
import { formatAUDate } from '../lib/dateUtils';

import { LegacyBanner } from '../components/LegacyBanner';

export default function TaxReturns() {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTaxYear, setFilterTaxYear] = useState('all');

  useEffect(() => {
    loadReturns();
  }, [filterStatus, filterTaxYear]);

  const loadReturns = async () => {
    try {
      const response = await taxReturnsAPI.listReturns({
        status: filterStatus !== 'all' ? filterStatus : undefined,
        tax_year: filterTaxYear !== 'all' ? filterTaxYear : undefined
      });
      setReturns(response.data || []);
    } catch (error) {
      toast.error('Failed to load tax returns');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <LegacyBanner module="Income Tax" />
      <fieldset disabled className="contents">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="tax-returns-title">
            Tax Returns
          </h1>
          <p className="text-muted-foreground mt-1">AI-powered individual tax return preparation and review</p>
        </div>
        <Button 
          onClick={() => navigate('/tax-returns/new')}
          className="bg-primary hover:bg-primary/90" 
          data-testid="new-return-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Tax Return
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground/90">Filters:</span>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="awaiting_documents">Awaiting Documents</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="ready_for_xero">Ready for Xero</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterTaxYear} onValueChange={setFilterTaxYear}>
              <SelectTrigger className="w-40" data-testid="filter-tax-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                <SelectItem value="2024-25">2024-25</SelectItem>
                <SelectItem value="2023-24">2023-24</SelectItem>
                <SelectItem value="2022-23">2022-23</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Returns Table */}
      <Card>
        <CardContent className="pt-6">
          {returns.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Tax Returns Yet</h3>
              <p className="text-muted-foreground mb-4">Start by creating a new tax return for a client</p>
              <Button onClick={() => navigate('/tax-returns/new')} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                New Tax Return
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Client</TableHead>
                    <TableHead className="font-semibold">Tax Year</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Questionnaire</TableHead>
                    <TableHead className="font-semibold">Documents</TableHead>
                    <TableHead className="font-semibold">Created</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {returns.map((taxReturn) => (
                    <TableRow key={taxReturn.id} className="hover:bg-muted/50" data-testid={`tax-return-row-${taxReturn.id}`}>
                      <TableCell className="font-medium">{taxReturn.client_name}</TableCell>
                      <TableCell className="font-mono">{taxReturn.tax_year}</TableCell>
                      <TableCell>
                        <TaxReturnStatusBadge status={taxReturn.status} />
                      </TableCell>
                      <TableCell>
                        {taxReturn.questionnaire_completed ? (
                          <span className="text-green-600 text-sm flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Complete
                          </span>
                        ) : (
                          <span className="text-muted-foreground/70 text-sm">Pending</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">{taxReturn.documents_uploaded || 0} uploaded</span>
                      </TableCell>
                      <TableCell>{formatAUDate(taxReturn.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          onClick={() => navigate(`/tax-returns/${taxReturn.id}/questionnaire`)}
                          size="sm"
                          variant="outline"
                          data-testid={`view-return-${taxReturn.id}`}
                        >
                          {taxReturn.questionnaire_completed ? 'View' : 'Continue'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      </fieldset>
    </div>
  );
}